import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import capitalize from 'scaleflow/utils/capitalize';
import type RoadmapService from 'scaleflow/services/roadmap';
import type IntlService from 'ember-intl/services/intl';
import type TeamModel from 'scaleflow/models/team';

type FieldOption = {
  label: string;
  value: string;
  checked: boolean;
};

export default class OperationsTeamsTeamRoadmapController extends Controller {
  queryParams = [
    'column',
    'row',
    'effort',
    'uncertainties',
    'waiting',
    'updatedAt',
    'tickets',
    'hiddenRows',
    'hiddenColumns',
  ];
  @tracked column = 'source';
  @tracked row = 'status';
  @tracked effort = true;
  @tracked uncertainties = true;
  @tracked tfos = true;
  @tracked waiting = true;
  @tracked updatedAt = true;
  @tracked tickets = true;
  @tracked slideoverIsOpen = false;
  @tracked team: TeamModel | undefined;
  @tracked hiddenRows = JSON.stringify({
    Parked: true,
    Archived: true,
    Draft: true,
  });
  @tracked hiddenColumns = '';

  @service declare roadmap: RoadmapService;
  @service declare intl: IntlService;

  get activeFieldsMap() {
    return {
      effort: this.effort,
      uncertainties: this.uncertainties,
      tfos: this.tfos,
      waiting: this.waiting,
      updatedAt: this.updatedAt,
      tickets: this.tickets,
    };
  }

  get fieldOptions(): FieldOption[] {
    return [
      {
        label: this.intl.t('dictionary.effort'),
        value: 'effort',
        checked: this.effort,
      },
      {
        label: this.intl.t('dictionary.uncertainty', { num: 2 }),
        value: 'uncertainties',
        checked: this.uncertainties,
      },
      {
        label: this.intl.t('dictionary.waiting-for-answers'),
        value: 'waiting',
        checked: this.waiting,
      },
      {
        label: this.intl.t('dictionary.last-updated'),
        value: 'updatedAt',
        checked: this.updatedAt,
      },
      {
        label: this.intl.t('_initiatives.tickets'),
        value: 'tickets',
        checked: this.tickets,
      },
    ];
  }

  get activeFields() {
    return this.fieldOptions.reduce((acc, field) => {
      if (field.checked === true) acc.push(field);
      return acc;
    }, [] as FieldOption[]);
  }

  get activeFieldsKeys() {
    return this.activeFields.reduce((acc, item) => {
      acc.push(item.value);
      return acc;
    }, [] as string[]);
  }

  @action
  setColumn(e: { value: string }) {
    this.column = e.value;
  }

  @action
  setRow(e: { value: string }) {
    this.row = e.value;
  }

  get options() {
    return [
      {
        label: capitalize(
          this.intl.t('_initiatives.source', {
            num: 1,
          }),
        ),
        value: 'source',
      },
      {
        label: capitalize(this.intl.t('_initiatives.status')),
        value: 'status',
      },
      {
        label: capitalize(
          this.intl.t('_initiatives.teams', {
            num: 1,
          }),
        ),
        value: 'teams',
      },
      {
        label: capitalize(
          this.intl.t('_initiatives.themes', {
            num: 1,
          }),
        ),
        value: 'theme',
      },
      {
        label: capitalize(this.intl.t('_initiatives.progress')),
        value: 'progress',
      },
      {
        label: capitalize(
          this.intl.t('dictionary.goals', {
            num: 1,
          }),
        ),
        value: 'goals',
      },
    ];
  }

  @action
  onFieldSelect(fields: string[]) {
    this.fieldOptions.forEach((item) => {
      const key = item.value as keyof typeof this.activeFieldsMap;
      if (fields.includes(item.value)) this[key] = true;
      else this[key] = false;
    });
  }

  @action
  updateRowFilters(hiddenRows: string) {
    this.hiddenRows = JSON.stringify(hiddenRows);
  }

  @action
  updateColumnFilters(hiddenColumns: string) {
    this.hiddenColumns = JSON.stringify(hiddenColumns);
  }

  get whitelist() {
    if (!this.team) return undefined;
    else
      return {
        teams: [this.team.name],
      };
  }
}
